/**
 * The work classes, and what passing one is worth.
 *
 * The Bully shape: optional, posted at a set point in the shift, five levels
 * deep, and the only thing passing one does is make you better at something the
 * game already asks of you. Nothing here is a new verb. Every perk below is a
 * number that an existing system reads and was already reading a constant for.
 *
 * Three rules this file exists to keep honest:
 *
 *   1. **Skipping costs nothing but the perk.** There is no standing on a
 *      class, no write-up for missing one, no note in the journal. A slot that
 *      passes unsat is a slot that passed. The whole point of optional is that
 *      the player who never sits one has played the game, and the game has to
 *      mean it.
 *   2. **A perk is a tier, not a switch.** `tiers[n]` is what you get for
 *      having passed `n` levels, so `tiers[0]` is *exactly* today's behaviour
 *      with no classes at all. That is what lets the benchmark run with every
 *      class unsat and come back to the same numbers.
 *   3. **One system each.** The throwing perk touches the throw and nothing
 *      else, the carry perk touches the hands and nothing else, the tidy perk
 *      touches one of the six marks. A perk that quietly improved two things
 *      would be a perk nobody could reason about, and the verify line says so.
 *
 * `opensAfter` and `window` are minutes measured from the *start of that day's
 * shift* rather than from the wall clock, because the roster runs a PM, an AM
 * and a double and a fixed hour would be mid-shift on one and the small hours
 * on another. Ninety minutes in is the same moment on all three: the first rush
 * is over and the room is not due yet.
 */

/** Minutes a sitting takes off the shift clock. The same for all three. */
export const CLASS_MINUTES = 25;

/** How many levels a class has. Five, and the fifth is meant to be hard. */
export const LEVELS = 5;

export const CLASSES = [
  /**
   * Trash basketball, in the break room.
   *
   * The can slides because a still can is a ranging problem you solve once. A
   * moving one is a timing problem you solve every shot, which is the thing the
   * perk is about: the throw in the game is at people and carts that are
   * walking, and a player who can only hit a parked target has not learned the
   * throw.
   */
  {
    id: 'trash-basketball',
    /** Where it is held, for its marker and its join prompt (a prop in `level.js`). */
    spot: 'break-table-a',
    name: 'Trash Basketball',
    room: 'break',
    where: 'the staff break room',
    blurb: 'Empties and balled-up trash, at a can somebody keeps sliding about.',
    game: 'basketball',
    perk: 'throwing',
    reads: 'the throw',
    opensAfter: 60,
    window: 120,
    /*
     * Ten shots every level. The can gets faster and the pass mark climbs, so
     * the difficulty is entirely in the two numbers that matter and none of it
     * is in giving you fewer chances, which would only make a bad round longer
     * to find out about.
     */
    levels: [
      { level: 1, shots: 10, target: 4, canSpeed: 0.9, canRange: 3.4 },
      { level: 2, shots: 10, target: 5, canSpeed: 1.3, canRange: 3.9 },
      { level: 3, shots: 10, target: 6, canSpeed: 1.8, canRange: 4.4 },
      { level: 4, shots: 10, target: 7, canSpeed: 2.4, canRange: 4.9 },
      { level: 5, shots: 10, target: 8, canSpeed: 3.1, canRange: 5.4 },
    ],
  },

  /**
   * Quick refresh, at the kitchen pass.
   *
   * A tray is not a bucket. What makes a loaded tray hard is that it is held
   * from underneath at one point, so where a thing lands on it matters as much
   * as how many things are on it, and the round is lost by a tray going over
   * rather than by a tray filling up. That is the same fact the carry perk is
   * about: a steady pair of hands keeps a load through a knock.
   */
  {
    id: 'quick-refresh',
    /** Where it is held, for its marker and its join prompt (a prop in `level.js`). */
    spot: 'kitchen-pass',
    name: 'Quick Refresh',
    room: 'kitchen',
    where: 'the kitchen pass',
    blurb: 'Cups, plates, flatware and napkin wads off the pass, onto one tray.',
    game: 'refresh',
    perk: 'carry',
    reads: 'the hands',
    opensAfter: 150,
    window: 120,
    /*
     * `tipAt` barely moves down the ladder, and the difficulty is nearly all in
     * the other three columns. That is deliberate and it was measured: the
     * first cut took it from 1 to 0.76 while the rate went up two and a half
     * times, and the two compounded into a fifth level that a player doing
     * everything right tipped four rounds in five. The rate, the fall and the
     * count are what should be getting harder; the tray is the same tray.
     */
    levels: [
      { level: 1, items: 14, target: 9, rate: 0.5, fall: 2.2, tipAt: 1 },
      { level: 2, items: 18, target: 12, rate: 0.65, fall: 2.6, tipAt: 0.97 },
      { level: 3, items: 22, target: 16, rate: 0.85, fall: 3, tipAt: 0.94 },
      { level: 4, items: 26, target: 20, rate: 1.05, fall: 3.5, tipAt: 0.91 },
      { level: 5, items: 30, target: 23, rate: 1.3, fall: 4, tipAt: 0.88 },
    ],
  },

  /**
   * Sucky Sucky, in a meeting room before it opens.
   *
   * The cord is the game. Vacuuming a floor is trivial; vacuuming a floor with
   * eighty feet of cable that catches every table leg you walk past is a route
   * problem, and the route is the reason this one earns a mark on the sheet
   * rather than a mark on your arms. The cord drags, wraps on a leg when you go
   * round one, and every wrap costs you reach from the socket.
   */
  {
    id: 'sucky-sucky',
    name: 'Sucky Sucky',
    room: 'ballroom',
    where: 'a meeting room before it opens',
    blurb: 'One vacuum, one socket, and a cord with opinions about table legs.',
    game: 'vacuum',
    perk: 'tidy',
    reads: 'the tidiness mark',
    opensAfter: 240,
    window: 120,
    /*
     * `cord` is in cells of the room grid and `seconds` is real time on the
     * panel, not shift minutes. Both come down as the trash goes up, so the
     * fifth level is a room you cannot finish by wandering.
     *
     * The shortest cord is twenty-one and the far corner of the room is twenty
     * one and a half from the socket, which is the floor the ladder is built
     * off: every level can reach every piece of trash *if the cable is
     * straight*, and a cable is never straight for long. The difficulty is in
     * the wraps and the clock rather than in a corner of the room that was
     * never yours, because an unreachable target is not a hard level, it is an
     * impossible one, and the first cut of this ladder made the fifth exactly
     * that.
     *
     * The clock is set against a measurement rather than against a feeling, and
     * it goes *up* with the level rather than down, which is the opposite of
     * what the first cut did. More trash and a shorter cord is more walking and
     * more unwinding, so a clock that tightened as well meant a fifth level
     * that ran out of time in twenty-one rooms out of twenty-five with a driver
     * that routed perfectly. What makes the top level hard is the cord and the
     * coverage it demands; the clock is there so a wandering round runs out,
     * not so a good one does.
     */
    levels: [
      { level: 1, trash: 10, cord: 40, tables: 3, seconds: 45, target: 0.6 },
      { level: 2, trash: 14, cord: 36, tables: 4, seconds: 52, target: 0.68 },
      { level: 3, trash: 18, cord: 32, tables: 5, seconds: 60, target: 0.76 },
      { level: 4, trash: 24, cord: 28, tables: 6, seconds: 70, target: 0.8 },
      { level: 5, trash: 30, cord: 24, tables: 7, seconds: 80, target: 0.85 },
    ],
  },
  /**
   * Quick Hands, at the kitchen pass, with a tray of mini chimis on it.
   *
   * The one class that is not a skill at a table: it is the building's own
   * rule about being seen, taught in a room where being seen costs you a round
   * instead of a write-up. Chefs walk the far side of the pass and you take one
   * when nobody has you, which is the same sentence as every theft in the game.
   *
   * It runs on the real perception. Not a cone written for a minigame — the
   * same module, the same falloff, the same "a witness who keeps looking gets
   * surer". Which is the whole argument for this class existing: what you learn
   * at the pass is true in the storeroom.
   */
  {
    id: 'quick-hands',
    /** Where it is held, for its marker and its join prompt (a prop in `level.js`). */
    spot: 'kitchen-pass',
    name: 'Quick Hands',
    room: 'kitchen',
    where: 'the kitchen pass',
    blurb: 'A tray of mini chimis, and chefs who keep walking past it.',
    game: 'hands',
    perk: 'hands',
    reads: 'what a witness makes of a take',
    opensAfter: 330,
    window: 120,
    /*
     * The four numbers that matter, and they pull in four directions. More
     * chefs is less floor without a cone on it; `spacing` is how far apart
     * they work, so a small one puts them all over the tray; `pace` and
     * `swing` decide how long a gap lasts; `glance` is how far their eyes come
     * off the pass at full stride, so a small one is somebody who barely
     * looks away; and
     * `caughtAt` is how sure anybody has to be before the round is over, which
     * comes down as everything else goes up.
     *
     * The `caughtAt` numbers are set against what perception can actually
     * produce rather than picked. From where the player stands, one chef with
     * a clear look is worth 0.41 straight away and can grow by at most another
     * 0.35 while they keep watching, so the ceiling on a single pair of eyes
     * is about 0.76. Level one at 0.72 means one bad glimpse is survivable if
     * you stand up straight and they move on; level five at 0.60 means it is
     * not. Below about 0.45 nothing would be passable at all, because the
     * first honest look at you would end the round.
     */
    levels: [
      { level: 1, chefs: 1, chimis: 10, target: 4, pace: 0.5, swing: 6, spacing: 16, glance: 1, seconds: 55, caughtAt: 0.82 },
      { level: 2, chefs: 2, chimis: 12, target: 5, pace: 0.6, swing: 6.5, spacing: 16, glance: 0.95, seconds: 58, caughtAt: 0.78 },
      { level: 3, chefs: 2, chimis: 14, target: 6, pace: 0.72, swing: 7, spacing: 14, glance: 0.9, seconds: 60, caughtAt: 0.74 },
      { level: 4, chefs: 3, chimis: 16, target: 7, pace: 0.85, swing: 7.5, spacing: 14, glance: 0.85, seconds: 62, caughtAt: 0.7 },
      { level: 5, chefs: 3, chimis: 18, target: 8, pace: 1, swing: 8, spacing: 12, glance: 0.8, seconds: 65, caughtAt: 0.66 },
    ],
  },
];

/**
 * What each perk is worth at each tier, indexed by levels passed.
 *
 * Index 0 is the untaught player and every figure in it is the constant the
 * system used before any of this existed. That is not a convention, it is the
 * contract: `bench.mjs` resets to a save with nothing passed, and the grid it
 * prints must not be able to move because somebody added a class.
 */
export const PERKS = Object.freeze({
  /**
   * The throw. Read by `sim/throwing.js`.
   *
   * `aim` is a cone, in radians, inside which a throw is allowed to find a
   * body it was nearly pointed at. Zero is today: the throw goes exactly where
   * the camera was looking and a near miss is a miss. Ten degrees at the top
   * is a player who can hit somebody walking, not a player who cannot miss.
   *
   * `range` multiplies the speed the arc comes out at, so it lengthens every
   * throw by the same fraction whatever the mass, and a heavy thing is still a
   * heave. Thirty per cent at the top, which is about eight feet on a bottle.
   */
  throwing: Object.freeze({
    aim: Object.freeze([0, 0.035, 0.07, 0.105, 0.14, 0.18]),
    range: Object.freeze([1, 1.06, 1.12, 1.18, 1.24, 1.3]),
  }),

  /**
   * The hands. Read by `sim/carry.js`.
   *
   * `extra` is added to the cap on a stack, which is the limit that only ever
   * binds on light things: a 35kg round is held to two by the weight budget and
   * could not care less what the cap says, while a linen is held to twelve by
   * the cap alone. So this is "more small items" in the only place small items
   * are actually counted, and it provably cannot put a third round in your
   * arms.
   *
   * `grip` is the fraction of a unit you keep hold of when something walks into
   * you. Not a chance: the hands carry the remainder as a debt and shed a unit
   * when it reaches one, so a grip of 0.2 is exactly one bump in five survived
   * and a grip of 1 is a load that never spills. A coin flip here would make
   * the perk unprovable, and an unprovable perk is a perk nobody trusts.
   */
  carry: Object.freeze({
    extra: Object.freeze([0, 1, 1, 2, 2, 3]),
    grip: Object.freeze([0, 0.2, 0.4, 0.6, 0.8, 1]),
  }),

  /**
   * The sheet. Read by `sim/grading.js`.
   *
   * `forgiveness` is how much of a stray the tidiness mark lets you off. It
   * multiplies the sins, so a clean room gains nothing at any tier and only
   * somebody who made a mess is helped by having been taught to clear one up.
   * Tidy carries 0.08 of the score, so the top tier is worth about a point on a
   * six-round room with two things out of place: enough to see, nowhere near
   * enough to buy a grade.
   */
  tidy: Object.freeze({
    forgiveness: Object.freeze([0, 0.08, 0.16, 0.24, 0.32, 0.4]),
  }),

  /**
   * What a witness makes of a take. Read by `sim/perception.js`.
   *
   * `quiet` multiplies the confidence of a claim about the player helping
   * themselves, and nothing else: not a claim about a thrown bottle, not a
   * claim about standing where you should not be, not anybody else's take.
   * One is the number perception has always used, so an untaught hand is
   * witnessed exactly as it always was.
   *
   * Forty per cent off at the top, which is a long way short of invisible.
   * That is deliberate: the claim still gets made, it still goes on the
   * record, and somebody standing next to you at arm's length is still sure.
   * What it buys is the middle of the room — the witness across the pass who
   * would have been sure enough to act is now somebody who thinks they saw
   * something.
   */
  hands: Object.freeze({
    quiet: Object.freeze([1, 0.92, 0.84, 0.76, 0.68, 0.6]),
  }),
});

export const CLASS_IDS = Object.freeze(CLASSES.map((entry) => entry.id));

export const byId = Object.freeze(
  Object.fromEntries(CLASSES.map((entry) => [entry.id, entry]))
);

/** The level rows for a class, or an empty list if there is no such class. */
export function levelsOf(id) {
  return byId[id]?.levels ?? [];
}

/** One level row. `level` is 1-based, the way the player is told it. */
export function levelOf(id, level) {
  return levelsOf(id).find((row) => row.level === level) ?? null;
}

export default CLASSES;
